import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";

/**
 * Ce emailuri am trimis către acest utilizator și ce s-a întâmplat cu ele.
 *
 * The question behind this panel gets asked about once a week, by phone: a
 * coach cannot get in, and nobody can say whether the activation email ever
 * reached them. Until now the only way to answer was database access.
 *
 * Two things sit here deliberately:
 *
 *  - "sent" is NOT "arrived". It means Brevo accepted the message. The
 *    delivered/opened/clicked columns come from Brevo's webhook afterwards,
 *    and a row with none of them is genuinely unknown rather than fine;
 *  - the account's activation state is at the top, because the list alone
 *    cannot tell "no email was ever sent" from "sent, read, and ignored" —
 *    and those have opposite answers.
 */

type EmailRow = {
  id: string;
  subject: string;
  template: string | null;
  status: string;
  error: string | null;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  bounce_reason: string | null;
  last_event: string | null;
  last_event_at: string | null;
  outcome: string;
};

type BrevoBlock = {
  blacklisted: boolean;
  contact_found: boolean;
  events: { event: string; date: string | null; subject: string | null; reason: string | null }[];
  error: string | null;
  verdict: string;
};

type Payload = {
  brevo: BrevoBlock | null;
  user: {
    id: string;
    email: string;
    full_name: string | null;
    email_verified: boolean | null;
    status: string;
    activation_pending: boolean;
    setup_token_expires: string | null;
    created_at: string;
  };
  logs: EmailRow[];
};

const when = (v: string | null | undefined) =>
  v
    ? new Date(v).toLocaleString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const OUTCOME_STYLE: Record<string, string> = {
  "a dat click": "bg-emerald-50 text-emerald-700 border-emerald-200",
  deschis: "bg-emerald-50 text-emerald-700 border-emerald-200",
  livrat: "bg-sky-50 text-sky-700 border-sky-200",
  trimis: "bg-slate-50 text-slate-600 border-slate-200",
  "în așteptare": "bg-amber-50 text-amber-700 border-amber-200",
  respins: "bg-rose-50 text-rose-700 border-rose-200",
  eșuat: "bg-rose-50 text-rose-700 border-rose-200",
};

/** Template codes, as the people reading this screen refer to them. */
const TEMPLATE_LABEL: Record<string, string> = {
  account_setup_coach: "Activare cont (antrenor)",
  account_setup_club_admin: "Activare cont (admin club)",
  account_setup_athlete: "Activare cont (sportiv)",
  account_setup_platform_admin: "Activare cont (admin platformă)",
  password_reset: "Resetare parolă",
  athlete_registration_verify: "Confirmare adresă de email",
  parent_registration_verify: "Confirmare adresă (părinte)",
  club_registration_verify: "Confirmare adresă (club)",
  welcome: "Bun venit",
  invitation: "Invitație",
  invoice: "Factură",
};

export default function UserEmailsPanel({ userId }: { userId: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Payload>(`/email-logs/user/${userId}`);
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca istoricul de emailuri.");
      setData(null);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  async function resend() {
    setResending(true);
    setResendMsg(null);
    try {
      const res = await api.post(`/email-logs/user/${userId}/resend-activation`);
      setResendMsg(res.data?.message || "Trimis.");
      await load();
    } catch (e: any) {
      setResendMsg(e?.response?.data?.error || "Nu am putut retrimite emailul.");
    }
    setResending(false);
  }

  if (loading && !data) return <p className="text-sm text-slate-400">Se încarcă istoricul de emailuri...</p>;

  if (error || !data) {
    return (
      <div className="p-4 bg-white border rounded-2xl border-slate-200">
        <p className="text-sm text-slate-500">{error}</p>
        <button onClick={load} className="mt-2 text-sm font-semibold text-indigo-600">
          Reîncearcă
        </button>
      </div>
    );
  }

  const u = data.user;
  const expired = u.setup_token_expires && new Date(u.setup_token_expires) < new Date();

  return (
    <div className="space-y-4">
      {/* Account state first: the list below means different things depending
          on it, and this is the part somebody is really asking about. */}
      <div className="p-4 bg-white border rounded-2xl border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-bold text-slate-800">Starea contului</h3>
          <span className="ml-auto font-mono text-xs text-slate-400">{u.email}</span>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <p>
            Adresă confirmată:{" "}
            <span className={u.email_verified ? "font-semibold text-emerald-700" : "font-semibold text-rose-700"}>
              {u.email_verified ? "da" : "nu"}
            </span>
          </p>
          <p>
            Cont: <span className="font-semibold text-slate-700">{u.status}</span>
          </p>
          {u.activation_pending && (
            <div className="p-3 mt-2 border rounded-xl border-amber-200 bg-amber-50">
              <p className="text-sm font-semibold text-amber-900">Invitația nu a fost folosită niciodată.</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-800">
                Contul a fost creat de un administrator, cu o parolă aleatoare pe care nimeni nu o
                cunoaște. Are nevoie de linkul de <strong>setare</strong> a parolei — nu de resetare.
                Dacă încearcă să se autentifice, i se spune acum exact asta.
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Link valabil până la: {when(u.setup_token_expires)}
                {expired && <strong> — expirat, retrimite-l.</strong>}
              </p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button
            onClick={resend}
            disabled={resending}
            className="px-3 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50"
          >
            {resending ? "Se trimite..." : "Retrimite emailul de activare"}
          </button>
          {resendMsg && <span className="text-sm text-slate-600">{resendMsg}</span>}
        </div>
      </div>

      {/* Brevo's own answer. Our rows stop at "handed over"; this is the only
          thing that says what happened after. */}
      {data.brevo && (
        <div
          className={`p-4 border-2 rounded-2xl ${
            data.brevo.blacklisted ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"
          }`}
        >
          <h3 className="font-bold text-slate-800">Ce spune Brevo</h3>

          {data.brevo.error ? (
            <p className="mt-2 text-sm text-slate-500">Nu am putut întreba Brevo: {data.brevo.error}</p>
          ) : (
            <>
              <p className="mt-2 text-sm font-semibold text-slate-700">{data.brevo.verdict}</p>

              {data.brevo.blacklisted && (
                <p className="mt-2 text-xs leading-relaxed text-rose-800">
                  Brevo ține o singură listă neagră per contact și ea acoperă și emailurile
                  tranzacționale. O adresă ajunge acolo după un singur bounce, un raport de spam sau
                  un click de dezabonare la orice mesaj anterior. De atunci, Brevo acceptă trimiterea
                  (201, fără eroare) și aruncă mesajul în tăcere. Scoate adresa din listă din Brevo →
                  Contacts, apoi retrimite activarea.
                </p>
              )}

              {data.brevo.events.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600">
                  {data.brevo.events.slice(0, 15).map((e, i) => (
                    <li key={i} className="flex flex-wrap gap-2">
                      <span className="font-mono text-slate-400">{when(e.date)}</span>
                      <span className="font-semibold">{e.event}</span>
                      <span className="truncate text-slate-500">{e.subject}</span>
                      {e.reason && <span className="text-rose-700">({e.reason})</span>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      <div className="overflow-hidden bg-white border rounded-2xl border-slate-200">
        <div className="flex items-center gap-3 p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Emailuri trimise ({data.logs.length})</h3>
          <button onClick={load} className="ml-auto text-xs font-semibold text-indigo-600">
            Reîncarcă
          </button>
        </div>

        {data.logs.length === 0 ? (
          <div className="p-4">
            <p className="text-sm text-slate-500">
              Niciun email înregistrat pentru această adresă. Dacă se aștepta unul de activare, nu a
              fost trimis deloc — verifică setările Brevo.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {data.logs.map((l) => (
                <li key={l.id} className="p-4">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800">{l.subject}</p>
                      <p className="text-xs text-slate-500">
                        {TEMPLATE_LABEL[l.template || ""] || l.template || "—"} · {when(l.created_at)}
                      </p>
                    </div>
                    <span
                      className={`ml-auto px-2 py-0.5 text-xs font-semibold border rounded-full ${
                        OUTCOME_STYLE[l.outcome] || "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {l.outcome}
                    </span>
                  </div>

                  <div className="flex flex-wrap mt-2 text-xs gap-x-4 gap-y-1 text-slate-500">
                    <span>trimis: {when(l.sent_at)}</span>
                    <span>livrat: {when(l.delivered_at)}</span>
                    <span>deschis: {when(l.opened_at)}</span>
                    <span>click: {when(l.clicked_at)}</span>
                  </div>

                  {l.bounced_at && (
                    <p className="mt-1 text-xs text-rose-700">
                      Respins {when(l.bounced_at)}
                      {l.bounce_reason ? ` — ${l.bounce_reason}` : ""}
                    </p>
                  )}
                  {l.error && <p className="mt-1 text-xs text-rose-700">Eroare: {l.error}</p>}
                </li>
              ))}
            </ul>

            {/* Said once, at the bottom, because the table above invites the
                opposite reading. */}
            {data.logs.every((l) => !l.last_event) && (
              <p className="p-4 text-xs leading-relaxed border-t text-slate-500 border-slate-100">
                Niciunul din emailurile de mai sus nu are evenimente de la Brevo. Fie webhook-ul Brevo
                nu e configurat către <code>/webhooks/brevo</code>, fie sunt mai vechi decât momentul
                în care am început să le înregistrăm. „Trimis” înseamnă că Brevo a acceptat mesajul,
                nu că a ajuns în inbox.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
